import { useEffect, useMemo } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import {
  ArrowLeft,
  ArrowRightLeft,
  Banknote,
  Check,
  Circle,
  CircleDot,
  FileText,
  Landmark,
  Receipt,
  ShieldCheck,
} from 'lucide-react';
import type { InvoiceStatus } from '@caratuva/shared-types';
import {
  api,
  ApiError,
  type OnRampAttemptWithInstructionsDTO,
  type PublicInvoiceDTO,
} from '../lib/api.ts';
import { isOnRampReady, isPaymentUnderway } from '../lib/buyer-flow.ts';
import { BankingInstructions } from '../components/BankingInstructions.tsx';

// Statuses where the buyer still needs to send their bank transfer — the only
// window in which we re-fetch + show the deposit instructions on this page.
const AWAITING_FUNDS: ReadonlySet<InvoiceStatus> = new Set<InvoiceStatus>([
  'on_ramp_pending',
  'on_ramp_failed',
  'partial_payment',
]);

// Tracking timeline. Polls `GET /v1/public/invoices/:publicId` every 10s
// and renders the six canonical milestones the buyer cares about. Each
// step has three states:
//   - completed: green check + timestamp (when known)
//   - active: animated dot, current invoice status maps here
//   - upcoming: muted outline circle
//
// Underpayment / KYC rejection / cancellation surface a banner above the
// timeline with the next action.
//
// There is deliberately no separate "on-chain confirmed" milestone: the
// ADR-0012 settlement rail goes fiat_received → off-ramp directly (the keeper's
// CREATE2 sweep is an internal mechanism, not a buyer-facing stage). The legacy
// `on_chain_confirmed` status (old ERC20FeeProxy rail) is still mapped onto the
// off-ramp step so historical invoices render sensibly.

type StepKey = 'invoice' | 'kyc' | 'onramp' | 'fiat' | 'offramp' | 'settled';

const STEP_ORDER: ReadonlyArray<StepKey> = [
  'invoice',
  'kyc',
  'onramp',
  'fiat',
  'offramp',
  'settled',
];

const STEP_ICONS: Record<StepKey, React.ComponentType<{ size: number; strokeWidth: number }>> = {
  invoice: FileText,
  kyc: ShieldCheck,
  onramp: Banknote,
  fiat: Landmark,
  offramp: ArrowRightLeft,
  settled: Receipt,
};

// Map invoice statuses to the step they currently sit on. Statuses that
// fall before a step show that step as upcoming. We collapse multiple
// flow states into the highest-completed step the buyer should see.
function activeStepFor(status: InvoiceStatus): StepKey {
  switch (status) {
    case 'draft':
    case 'awaiting_approval':
    case 'approved':
    case 'awaiting_buyer':
      return 'invoice';
    case 'buyer_kyc_pending':
    case 'buyer_kyc_rejected':
      return 'kyc';
    case 'buyer_kyc_approved':
    case 'fx_quoted':
    case 'fx_expired':
      return 'onramp';
    case 'on_ramp_pending':
    case 'on_ramp_failed':
    case 'partial_payment':
      return 'fiat';
    // Fiat is in; the off-ramp to the seller is the active leg. `fiat_received`
    // (ADR-0012 rail) and the legacy `on_chain_confirmed` both land here.
    case 'fiat_received':
    case 'on_chain_confirmed':
      return 'offramp';
    case 'offramp_pending':
    case 'offramp_failed':
      return 'settled';
    case 'settled':
      return 'settled';
    default:
      return 'invoice';
  }
}

// True when the active step is "in progress". `settled` is the only step
// where reaching it means it's done (the active marker still sits there).
function isActiveStepCompleted(status: InvoiceStatus, step: StepKey): boolean {
  if (step !== activeStepFor(status)) return false;
  return status === 'settled';
}

// Step is completed when the active step is past it in STEP_ORDER, OR
// when the active step is this step AND the status indicates done.
function stepCompleted(status: InvoiceStatus, step: StepKey): boolean {
  const active = activeStepFor(status);
  const stepIdx = STEP_ORDER.indexOf(step);
  const activeIdx = STEP_ORDER.indexOf(active);
  if (stepIdx < activeIdx) return true;
  return isActiveStepCompleted(status, step);
}

function isAlertable(status: InvoiceStatus): {
  kind: 'kycRejected' | 'underpayment' | 'expired' | 'cancelled' | null;
} {
  if (status === 'buyer_kyc_rejected') return { kind: 'kycRejected' };
  if (status === 'partial_payment') return { kind: 'underpayment' };
  if (status === 'expired') return { kind: 'expired' };
  if (status === 'cancelled') return { kind: 'cancelled' };
  return { kind: null };
}

function TrackingPage() {
  const { publicId } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading, error, isFetching } = useQuery<PublicInvoiceDTO, ApiError>({
    queryKey: ['public-invoice', publicId],
    queryFn: () => api.getPublicInvoice(publicId),
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  // Re-fetch the bank deposit instructions only while the buyer still owes the
  // transfer, so they can complete it from here after leaving the on-ramp
  // screen. 401 (no buyer session) / 404 (no attempt yet) collapse to null so
  // the page never hard-errors — the panel just doesn't render.
  const awaitingFunds = data ? AWAITING_FUNDS.has(data.status) : false;
  const onRamp = useQuery<OnRampAttemptWithInstructionsDTO | null, ApiError>({
    queryKey: ['on-ramp-attempt', publicId],
    enabled: awaitingFunds,
    retry: false,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      try {
        return await api.getOnRampAttempt(publicId);
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 401)) return null;
        throw err;
      }
    },
  });

  // When a poll observes the invoice is on-ramp-ready (buyer KYC approved and
  // the on-ramp has not started yet), the on-ramp is the buyer's next action —
  // send them there automatically rather than leaving them on the read-only
  // timeline. Mirrors nextStopForBuyer's buyer_kyc_approved -> /on-ramp routing
  // for the case where the status advances while the buyer is already on
  // /tracking. `replace` keeps tracking out of history so Back doesn't bounce
  // them here.
  const status = data?.status;
  useEffect(() => {
    if (status && isOnRampReady(status)) {
      void navigate({ to: '/r/$publicId/on-ramp', params: { publicId }, replace: true });
    }
  }, [status, navigate, publicId]);

  const alert = useMemo(() => (data ? isAlertable(data.status) : { kind: null }), [data]);

  // Once the payment is in flight or the invoice is terminal, /r/:id redirects
  // straight back here (see r.$publicId.index.tsx), so a "Back to invoice" link
  // would be a dead bounce. Hide it in those states; it stays useful for the
  // earlier flow stages (e.g. KYC under review) that can still reach tracking.
  const showBackToInvoice = !(data && isPaymentUnderway(data.status));

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      {showBackToInvoice && (
        <Link
          to="/r/$publicId"
          params={{ publicId }}
          className="inline-flex items-center gap-1 text-sm text-[var(--color-navy-600)] hover:text-[var(--color-navy-900)]"
        >
          <ArrowLeft size={16} strokeWidth={1.5} aria-hidden="true" />
          <FormattedMessage id="tracking.cta.back" />
        </Link>
      )}

      <div className="mt-4 rounded-xl border border-[var(--color-navy-100)] bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-xl font-semibold text-[var(--color-navy-900)]">
            <FormattedMessage id="tracking.heading" />
          </h1>
          {isFetching && (
            <span
              className="rounded-full bg-[var(--color-navy-50)] px-2 py-0.5 text-xs text-[var(--color-navy-500)]"
              aria-live="polite"
            >
              <FormattedMessage id="tracking.refreshing" />
            </span>
          )}
        </div>

        {isLoading && (
          <p className="mt-4 text-sm text-[var(--color-navy-600)]">
            <FormattedMessage id="common.loading" />
          </p>
        )}

        {error && (
          <p className="mt-4 text-sm text-[var(--color-danger)]">
            {error.status === 404 ? (
              <FormattedMessage id="invoice.error.notFound" />
            ) : (
              error.message
            )}
          </p>
        )}

        {data && (
          <>
            <div className="mt-4 flex items-center gap-2 text-sm">
              <span className="text-[var(--color-navy-500)]">
                <FormattedMessage id="invoice.status.label" />
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-navy-100)] px-2.5 py-0.5 font-mono text-xs text-[var(--color-navy-800)]">
                {data.status}
              </span>
            </div>

            {alert.kind && <AlertBanner kind={alert.kind} publicId={publicId} />}

            {awaitingFunds && onRamp.data?.bankingInstructions && (
              <section className="mt-6">
                <h2 className="text-sm font-medium uppercase tracking-wide text-[var(--color-navy-500)]">
                  <FormattedMessage id="onramp.instructions.heading" />
                </h2>
                <p className="mb-4 mt-1 text-sm text-[var(--color-navy-600)]">
                  <FormattedMessage id="onramp.instructions.subheading" />
                </p>
                <BankingInstructions
                  instructions={onRamp.data.bankingInstructions}
                  fiatAmount={onRamp.data.fiatAmount}
                  fiatCurrency={onRamp.data.fiatCurrency}
                />
              </section>
            )}

            <Timeline status={data.status} />
          </>
        )}
      </div>
    </div>
  );
}

function Timeline({ status }: { status: InvoiceStatus }): React.ReactElement {
  const active = activeStepFor(status);
  return (
    <section className="mt-6">
      <h2 className="text-sm font-medium uppercase tracking-wide text-[var(--color-navy-500)]">
        <FormattedMessage id="tracking.timeline.heading" />
      </h2>
      <ol className="relative mt-4 space-y-5 border-l border-[var(--color-navy-100)] pl-6">
        {STEP_ORDER.map((step) => {
          const Icon = STEP_ICONS[step];
          const completed = stepCompleted(status, step);
          const isActive = !completed && step === active;
          return (
            <li key={step} className="relative">
              <span
                className={
                  'absolute -left-[33px] flex h-6 w-6 items-center justify-center rounded-full border-2 ' +
                  (completed
                    ? 'border-[var(--color-success)] bg-[var(--color-success)] text-white'
                    : isActive
                      ? 'border-[var(--color-navy-800)] bg-white text-[var(--color-navy-800)]'
                      : 'border-[var(--color-navy-200)] bg-white text-[var(--color-navy-300)]')
                }
                aria-hidden="true"
              >
                {completed ? (
                  <Check size={14} strokeWidth={2.5} />
                ) : isActive ? (
                  <CircleDot size={14} strokeWidth={2} />
                ) : (
                  <Circle size={14} strokeWidth={2} />
                )}
              </span>
              <div
                className={
                  'flex items-center gap-2 ' +
                  (completed
                    ? 'text-[var(--color-navy-900)]'
                    : isActive
                      ? 'text-[var(--color-navy-900)]'
                      : 'text-[var(--color-navy-400)]')
                }
              >
                <Icon size={16} strokeWidth={1.5} />
                <span className="text-sm font-medium">
                  <FormattedMessage id={`tracking.step.${step}`} />
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

// Per-step timestamp slot. The public invoice DTO does not yet carry the
// event timeline (that lives on the authenticated payments projection),
// so timestamps remain blank for now and are wired in when the public
// DTO ships event dates. The visual layout already reserves the space.

function AlertBanner({
  kind,
  publicId,
}: {
  kind: 'kycRejected' | 'underpayment' | 'expired' | 'cancelled';
  publicId: string;
}): React.ReactElement {
  const headingKey = `tracking.alert.${kind}.heading` as const;
  const bodyKey = `tracking.alert.${kind}.body` as const;
  return (
    <div className="mt-4 rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 p-4 text-sm text-[var(--color-navy-900)]">
      <p className="font-medium">
        <FormattedMessage id={headingKey} />
      </p>
      <p className="mt-1 text-[var(--color-navy-700)]">
        <FormattedMessage id={bodyKey} />
      </p>
      <div className="mt-3">
        {kind === 'kycRejected' ? (
          <Link
            to="/r/$publicId/kyc"
            params={{ publicId }}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--color-navy-800)] px-3 py-1.5 text-sm font-medium text-[var(--color-navy-900)] hover:bg-[var(--color-navy-50)]"
          >
            <FormattedMessage id="tracking.alert.kycRejected.cta" />
          </Link>
        ) : kind === 'underpayment' ? (
          <a
            href="mailto:support@caratuva.com"
            className="inline-flex items-center gap-1 rounded-md border border-[var(--color-navy-800)] px-3 py-1.5 text-sm font-medium text-[var(--color-navy-900)] hover:bg-[var(--color-navy-50)]"
          >
            <FormattedMessage id="tracking.alert.contactSupport" />
          </a>
        ) : null}
      </div>
    </div>
  );
}

// Re-exporting at the bottom keeps the route tree generator happy when we
// later add more named utilities at the top of the file.
export const Route = createFileRoute('/r/$publicId/tracking')({
  component: TrackingPage,
});

// Test-only export: keep the pure helpers reachable from a future test file
// without bundling React Testing Library yet. The module-level ESM exports
// don't change the route runtime contract.
export const __test__ = { activeStepFor, stepCompleted, isAlertable };
